import axiosInstance from "./axiosInstance";

export interface CutoffConfigApi {
  publicId: string;
  daysBeforeCutoff: number;
  effectiveFrom: string;
  effectiveTo: string;
  notes: string;
}

export interface CutoffConfigListResponse {
  items: CutoffConfigApi[];
  pageNo: number;
  pageSize: number;
  totalCount: number;
  pageNumber: number;
}

export interface CreateCutoffConfigRequest {
  daysBeforeCutoff: number;
  effectiveFrom: string;
  effectiveTo: string;
  notes: string;
}

export interface CreateCutoffConfigResponse {
  message: string;
}

export interface UpdateCutoffConfigRequest {
  publicId: string;
  daysBeforeCutoff: number;
  effectiveFrom: string;
  effectiveTo: string;
  notes: string;
}

export interface UpdateCutoffConfigResponse {
  message: string;
}

export interface TourOperatorType {
  tourOperatorTypeId: number;
  typeName: string;
  code: string;
  category: string;
  isActive: boolean;
  description: string;
}

export interface TourOperator {
  tourOperatorId: number;
  itsNumber: string;
  firstName: string;
  middleName: string;
  lastName: string;
  officeName: string;
}

export interface CutoffDetailApi {
  id: number;
  publicId: string;
  tourOperatorTypeId: number;
  tourOperatorId: number | null;
  cutoffDays: number;
  effectiveFrom: string;
  effectiveTo: string;
  isActive: boolean;
}

export interface CutoffWithDetailsResponse {
  publicId: string;
  daysBeforeCutoff: number;
  effectiveFrom: string;
  effectiveTo: string;
  notes: string;
  details: CutoffDetailApi[];
}

export const getCutoffConfigurationWithDetails = async (
  publicId: string
): Promise<CutoffWithDetailsResponse> => {
  const res = await axiosInstance.get(
    `/cutoff-configuration/${publicId}/with-details`,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export interface CreateCutoffDetailRequest {
  masterPublicId: string;
  tourOperatorTypeId: number | null;
  tourOperatorId: number | null;
  cutoffDays: number;
  effectiveFrom: string;
  effectiveTo: string;
  isActive: boolean;
}

export interface UpdateCutoffDetailRequest {
  publicId: string;
  tourOperatorTypeId: number | null;
  tourOperatorId: number | null;
  cutoffDays: number;
  effectiveFrom: string;
  effectiveTo: string;
  isActive: boolean;
}

export const getAuthHeader = () => {
  const token = localStorage.getItem("fhims_auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getCutoffConfigurations = async (
  pageNo = 1,
  pageSize = 20
): Promise<CutoffConfigListResponse> => {
  const res = await axiosInstance.get(
    `/cutoff-configuration/list?PageNo=${pageNo}&PageSize=${pageSize}`,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const createCutoffConfiguration = async (
  payload: CreateCutoffConfigRequest
): Promise<CreateCutoffConfigResponse> => {
  const res = await axiosInstance.post(
    `/cutoff-configuration/create`,
    payload,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const updateCutoffConfiguration = async (
  payload: UpdateCutoffConfigRequest
): Promise<UpdateCutoffConfigResponse> => {
  const res = await axiosInstance.put(
    `/cutoff-configuration/update`,
    payload,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const deleteCutoffConfiguration = async (
  publicId: string
): Promise<{ message: string }> => {
  const res = await axiosInstance.delete(
    `/cutoff-configuration/${publicId}`,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const getTourOperatorTypes = async (): Promise<TourOperatorType[]> => {
  const res = await axiosInstance.get(
    `/tour-operator-type`,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const getTourOperators = async (): Promise<TourOperator[]> => {
  const res = await axiosInstance.get(`/tour-operator`, {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const createCutoffDetail = async (
  payload: CreateCutoffDetailRequest
): Promise<{ message: string }> => {
  const res = await axiosInstance.post(
    `/cutoff-configuration/details`,
    payload,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const updateCutoffDetail = async (
  payload: UpdateCutoffDetailRequest
): Promise<{ message: string }> => {
  const res = await axiosInstance.put(
    `/cutoff-configuration/details`,
    payload,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const deleteCutoffDetail = async (
  publicId: string
): Promise<{ message: string }> => {
  const res = await axiosInstance.delete(
    `/cutoff-configuration/details/${publicId}`,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};